import React, { useState } from 'react';
import DynamicProgress from './DynamicProgress';
import AmbassadorDonorsModal from './AmbassadorDonorsModal';

/**
 * Personal ambassador meter — reads clearly as one individual's progress even
 * when the campaign's own meter sits on the same page. Shares the campaign's
 * brand color (accent) so the two feel like one system, while the framing keeps
 * the personal one distinct. Several visual variants (see AMB_CARD_VARIANTS).
 *
 * Shared by the live renderer (PublicCampaign) and the builder preview
 * (BlockRenderer) so the two never drift.
 */

export const AMB_CARD_VARIANTS: { value: string; label: string }[] = [
  { value: 'card',      label: 'כרטיס רך (ברירת מחדל)' },
  { value: 'spotlight', label: 'זרקור — רקע צבעוני בולט' },
  { value: 'stats',     label: 'נתונים — אריחי סטטיסטיקה' },
  { value: 'compact',   label: 'קומפקטי — שורה אחת' },
  { value: 'outline',   label: 'קו מתאר — נקי ומינימלי' },
  { value: 'plain',     label: 'פשוט — מד בלבד' },
];

/** hex (#rgb / #rrggbb) → rgba() at alpha. Falls back to the raw value for non-hex. */
function hexA(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
/** Darken a #rrggbb color toward black by `amt` (0..1). Used for the spotlight gradient. */
function darken(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amt));
  const g = Math.round(((n >> 8) & 255) * (1 - amt));
  const b = Math.round((n & 255) * (1 - amt));
  return `rgb(${r}, ${g}, ${b})`;
}

export interface AmbassadorStatsCardProps {
  p: any;                 // block.props
  s: any;                 // block.style
  baseStyle: React.CSSProperties;
  accent: string;         // brand / campaign primary
  name: string;
  raised: number;
  goal: number;
  donors: number;
  activeCurrency?: any;
  animate?: boolean;
  editLocale?: any;
  scale?: number;
  sample?: boolean;       // builder preview → append the "only on ambassador link" hint
  // When both are present the donor-count chip becomes a button that opens the
  // per-ambassador donor list. Absent in the builder preview (no live data).
  campaignSlug?: string;
  ambassadorSlug?: string;
}

const AmbassadorStatsCard: React.FC<AmbassadorStatsCardProps> = ({
  p, s, baseStyle, accent, name, raised, goal, donors,
  activeCurrency, animate, editLocale, scale, sample,
  campaignSlug, ambassadorSlug,
}) => {
  const [donorsOpen, setDonorsOpen] = useState(false);
  const acc = p.accentColor || accent || '#2563eb';
  const variant: string = AMB_CARD_VARIANTS.some(v => v.value === p.cardVariant) ? p.cardVariant : 'card';
  const showName = p.showName !== false;
  const showDonors = p.showDonors !== false;
  const showAvatar = p.showAvatar !== false && variant !== 'plain';
  const subtitle = p.subtitle ?? 'שגריר בקמפיין';
  const showTarget = p.showTarget !== false && goal > 0;

  const nameLine = String(p.nameTemplate || 'היעד של {name}').replace('{name}', name);
  const donorsLine = String(p.donorsTemplate || '{count} תורמים').replace('{count}', Number(donors || 0).toLocaleString());
  const txtColor = (s.textColor as string) || (baseStyle.color as string) || '#1f2937';
  const initial = (name || '').trim().charAt(0) || '🤝';
  const sym = p.currency || '₪';
  const fmt = (n: number) => `${sym}${Number(n || 0).toLocaleString()}`;

  // Coordinate the bar with the accent by default; a bar color the owner
  // explicitly changed away from the shared default still wins.
  const barColor = p.barColor && p.barColor !== '#2563eb' ? p.barColor : acc;

  const meter = (over?: any, styleOver?: any, baseOver?: any) => (
    <DynamicProgress
      props={{ ...p, dataSource: 'campaign', showTarget, barColor, ...over }}
      style={{ ...s, ...styleOver }}
      baseStyle={{ ...baseStyle, ...baseOver }}
      scale={scale}
      campaignData={{ raised, goal }}
      activeCurrency={activeCurrency}
      editLocale={editLocale}
      animateValue={animate}
      playEntrance
    />
  );

  const avatar = (size: number, bg: string, fg: string, ring?: string) => (
    <div style={{
      flex: '0 0 auto', width: size, height: size, borderRadius: '50%', background: bg, color: fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: Math.round(size * 0.43),
      boxShadow: ring || `0 2px 8px ${hexA(acc, 0.4)}`,
    }}>{initial}</div>
  );

  const nameBlock = (color: string, subColor: string, big?: boolean) => (
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: big ? 19 : (s.fontSize ? Number(s.fontSize) + 1 : 17), color, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {nameLine}
      </div>
      {subtitle && <div style={{ fontSize: 12.5, color: subColor, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );

  // The donor chip becomes a button (opens the donor list) only on the live page
  // where we have the slugs and at least one donor to show.
  const donorsClickable = showDonors && Number(donors) > 0 && !!campaignSlug && !!ambassadorSlug;
  const openDonors = () => setDonorsOpen(true);

  const donorsModal = (donorsOpen && donorsClickable) ? (
    <AmbassadorDonorsModal
      campaignSlug={campaignSlug!}
      ambassadorSlug={ambassadorSlug!}
      ambName={name}
      accent={acc}
      currencySymbol={sym}
      onClose={() => setDonorsOpen(false)}
    />
  ) : null;

  const donorChip = (bg: string, color: string, justify: string = 'flex-start') => {
    if (!showDonors) return null;
    const chipStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, background: bg, color, fontWeight: 600, fontSize: 13, lineHeight: 1.4, fontFamily: 'inherit' };
    const inner = (
      <>
        <span aria-hidden>👥</span>{donorsLine}
        {donorsClickable && <span aria-hidden style={{ opacity: 0.75, marginInlineStart: 2, fontSize: 15 }}>›</span>}
      </>
    );
    return (
      <div style={{ display: 'flex', justifyContent: justify, marginTop: 12 }}>
        {donorsClickable ? (
          <button type="button" onClick={openDonors} title="לחצו לרשימת התורמים" style={{ ...chipStyle, border: 'none', cursor: 'pointer' }}>{inner}</button>
        ) : (
          <span style={chipStyle}>{inner}</span>
        )}
        {donorsModal}
      </div>
    );
  };

  const sampleNote = sample ? (
    <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
      מוצג רק בקישור של שגריר · נתונים לדוגמה
    </div>
  ) : null;

  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

  // ── plain: minimal, centered — meter only ──────────────────────────────
  if (variant === 'plain') {
    return (
      <div style={{ width: '100%' }}>
        {showName && name && (
          <div style={{ textAlign: (s.textAlign as any) || 'center', fontWeight: 700, fontSize: s.fontSize ? Number(s.fontSize) + 2 : 18, color: txtColor, marginBottom: 8 }}>{nameLine}</div>
        )}
        {meter()}
        {donorChip(hexA(acc, 0.12), acc, 'center')}
        {sampleNote}
      </div>
    );
  }

  // ── spotlight: filled brand gradient, white content ────────────────────
  if (variant === 'spotlight') {
    return (
      <div style={{
        width: '100%', boxSizing: 'border-box', color: '#fff',
        background: `linear-gradient(135deg, ${acc}, ${darken(acc, 0.28)})`,
        borderRadius: s.borderRadius ?? 20, padding: s.padding ?? 20,
        boxShadow: `0 10px 30px ${hexA(acc, 0.35)}`,
      }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {showAvatar && avatar(52, 'rgba(255,255,255,0.18)', '#fff', '0 0 0 2px rgba(255,255,255,0.35)')}
            {nameBlock('#fff', 'rgba(255,255,255,0.85)', true)}
          </div>
        )}
        {meter(
          { barColor: '#ffffff', barTrackColor: 'rgba(255,255,255,0.25)', pctColor: '#ffffff' },
          { textColor: '#ffffff' },
          { color: '#ffffff' },
        )}
        {donorChip('rgba(255,255,255,0.2)', '#fff')}
        {sampleNote}
      </div>
    );
  }

  // ── stats: numbers-forward — three stat tiles above a thin bar ─────────
  if (variant === 'stats') {
    const tile = (label: string, value: string, strong?: boolean, onClick?: () => void) => {
      const tileStyle: React.CSSProperties = { flex: 1, minWidth: 0, textAlign: 'center', padding: '10px 6px', borderRadius: 12, background: hexA(acc, strong ? 0.12 : 0.055), border: `1px solid ${hexA(acc, 0.14)}`, fontFamily: 'inherit' };
      const content = (
        <>
          <div style={{ fontWeight: 800, fontSize: 18, color: (strong || onClick) ? acc : txtColor, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}{onClick && ' ›'}</div>
          <div style={{ fontSize: 11.5, color: hexA(txtColor, 0.6), marginTop: 2 }}>{label}</div>
        </>
      );
      return onClick
        ? <button type="button" onClick={onClick} title="לחצו לרשימת התורמים" style={{ ...tileStyle, cursor: 'pointer' }}>{content}</button>
        : <div style={tileStyle}>{content}</div>;
    };
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', background: s.bgTransparent === false ? (s.bgColor || '#fff') : '#fff', border: `1px solid ${hexA(acc, 0.18)}`, borderRadius: s.borderRadius ?? 18, padding: s.padding ?? 18, boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {showAvatar && avatar(44, acc, '#fff')}
            {nameBlock(txtColor, hexA(txtColor, 0.6))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {tile('גויסו', fmt(raised), true)}
          {showTarget && tile('היעד', fmt(goal))}
          {showDonors && tile('תורמים', Number(donors || 0).toLocaleString(), false, donorsClickable ? openDonors : undefined)}
        </div>
        {meter({ barThickness: p.barThickness || 12, showPercent: true })}
        {donorsModal}
      </div>
    );
  }

  // ── compact: single horizontal row ─────────────────────────────────────
  if (variant === 'compact') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12, background: s.bgTransparent === false ? (s.bgColor || '#fff') : hexA(acc, 0.05), border: `1px solid ${hexA(acc, 0.18)}`, borderRadius: s.borderRadius ?? 14, padding: s.padding ?? 12 }}>
        {showAvatar && avatar(40, acc, '#fff')}
        <div style={{ flex: 1, minWidth: 0 }}>
          {showName && name && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 14.5, color: txtColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameLine}</span>
              <span style={{ fontWeight: 800, fontSize: 13.5, color: acc, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{pct}%</span>
            </div>
          )}
          {meter({ barThickness: p.barThickness || 10, showPercent: false, showTarget: false, label: '' })}
          {showDonors && (
            <div style={{ fontSize: 12, color: hexA(txtColor, 0.7), marginTop: 6 }}>
              {fmt(raised)}{showTarget ? ` מתוך ${fmt(goal)}` : ''} ·{' '}
              {donorsClickable ? (
                <button type="button" onClick={openDonors} title="לחצו לרשימת התורמים"
                  style={{ border: 'none', background: 'none', padding: 0, margin: 0, cursor: 'pointer', color: acc, fontWeight: 600, fontSize: 12, fontFamily: 'inherit' }}>
                  👥 {donorsLine} ›
                </button>
              ) : (
                <span>👥 {donorsLine}</span>
              )}
              {donorsModal}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── outline: clean bordered card, no fill ──────────────────────────────
  if (variant === 'outline') {
    return (
      <div style={{ width: '100%', boxSizing: 'border-box', background: s.bgTransparent === false ? (s.bgColor || 'transparent') : 'transparent', border: `1.5px solid ${hexA(acc, 0.5)}`, borderRadius: s.borderRadius ?? 16, padding: s.padding ?? 18 }}>
        {showName && name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {showAvatar && avatar(44, 'transparent', acc, `inset 0 0 0 2px ${acc}`)}
            {nameBlock(txtColor, hexA(txtColor, 0.6))}
          </div>
        )}
        {meter()}
        {donorChip(hexA(acc, 0.1), acc)}
        {sampleNote}
      </div>
    );
  }

  // ── card (default): soft branded panel ─────────────────────────────────
  const cardBg = s.bgTransparent === false ? (s.bgColor || '#ffffff') : `linear-gradient(180deg, ${hexA(acc, 0.06)}, ${hexA(acc, 0.015)})`;
  return (
    <div style={{ width: '100%', boxSizing: 'border-box', background: cardBg, border: `1px solid ${hexA(acc, 0.22)}`, borderRadius: s.borderRadius ?? 18, padding: s.padding ?? 18, boxShadow: '0 6px 22px rgba(0,0,0,0.06)' }}>
      {showName && name && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {showAvatar && avatar(44, acc, '#fff')}
          {nameBlock(txtColor, hexA(txtColor, 0.6))}
        </div>
      )}
      {meter()}
      {donorChip(hexA(acc, 0.12), acc)}
      {sampleNote}
    </div>
  );
};

export default AmbassadorStatsCard;
